import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG = "/ops-photo-hero.png";

const lineVar = {
  hidden: { opacity: 0, y: 30, filter: "blur(8px)" },
  show: (i) => ({
    opacity: 1, y: 0, filter: "blur(0px)",
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export default function OpsHero() {
  return (
    <section id="top" data-testid="ops-hero" className="relative overflow-hidden bg-[#1A2E4A]">
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#D9FF3C]/10 blur-[120px] pointer-events-none"
      />
      <motion.div
        animate={{ scale: [1.05, 1, 1.05], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -bottom-40 -right-40 h-[440px] w-[440px] rounded-full bg-[#D9FF3C]/6 blur-[140px] pointer-events-none"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-6 flex flex-col gap-7">
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#D9FF3C] inline-flex items-center gap-2"
            >
              <motion.span
                animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block h-1.5 w-1.5 rounded-full bg-[#D9FF3C]"
              />
              OpsIQ · Accra, Ghana
            </motion.div>

            <h1 data-testid="ops-hero-headline" className="font-editorial text-5xl md:text-6xl lg:text-7xl xl:text-[80px] leading-[1.02] tracking-tight text-[#F1ECE0]">
              <motion.span variants={lineVar} initial="hidden" animate="show" custom={0} className="block">
                Know your business.
              </motion.span>
              <motion.span variants={lineVar} initial="hidden" animate="show" custom={1} className="block italic">
                Lead with confidence.
              </motion.span>
            </h1>

            <motion.p variants={lineVar} initial="hidden" animate="show" custom={2}
              className="text-lg md:text-xl leading-[1.35] text-[#D9FF3C] font-editorial italic max-w-xl"
            >
              Operational Intelligence for Smarter Decisions.
            </motion.p>

            <motion.p variants={lineVar} initial="hidden" animate="show" custom={3}
              className="text-base md:text-lg leading-relaxed text-[#F1ECE0]/75 max-w-xl"
            >
              OpsIQ is a Ghana-based Operational Intelligence consultancy that helps
              organisations gain greater visibility into their operations through customised
              dashboards, KPI frameworks, performance management systems, and operational
              reporting solutions. We help leaders understand their business, monitor performance,
              identify opportunities, and make informed decisions with confidence.
            </motion.p>

            <motion.div variants={lineVar} initial="hidden" animate="show" custom={4}
              className="flex flex-wrap items-center gap-3 pt-1"
            >
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 300, damping: 22 }}>
                <Link
                  to="/book"
                  data-testid="ops-hero-primary-cta"
                  className="inline-flex items-center gap-2 px-6 py-4 bg-[#D9FF3C] text-[#142645] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#E4FF66] transition-colors"
                >
                  Book a consultation <ArrowUpRight size={16} />
                </Link>
              </motion.div>
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 300, damping: 22 }}>
                <Link
                  to="/solutions"
                  data-testid="ops-hero-secondary-cta"
                  className="inline-flex items-center gap-2 px-6 py-4 border border-[#F1ECE0]/25 text-[#F1ECE0] text-xs uppercase tracking-[0.22em] font-semibold hover:border-[#F1ECE0] hover:bg-[#F1ECE0]/5 transition-colors"
                >
                  Explore our solutions
                </Link>
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative overflow-hidden border border-white/10">
              <img
                src={HERO_IMG}
                alt="Ghanaian executive team reviewing dashboards in a modern boardroom"
                data-testid="hero-image"
                className="w-full h-[420px] md:h-[560px] lg:h-[620px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1A2E4A]/70 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 bg-[#D9FF3C] text-[#142645] px-3 py-1.5 text-[10px] font-plex-mono uppercase tracking-[0.22em] font-semibold flex items-center gap-2">
                <motion.span
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity }}
                  className="inline-block h-1.5 w-1.5 rounded-full bg-[#142645]"
                />
                Live decisions
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <div className="bg-[#142645]/80 backdrop-blur-sm px-3 py-2 border border-white/10">
                  <div className="text-[9px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C]">Executive review</div>
                  <div className="text-[11px] text-[#F1ECE0] mt-0.5">Monday, 08:30 · Accra HQ</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
